import styled, { css } from 'styled-components';
import NextImage from 'next/image';
import campaignHeading from 'Common/styles/campaignHeading';

import Section from 'Elements/Section';

import {
    Container as SupportBlockContainer,
    Wrapper as SourceSupportBlockWrapper
} from 'Campaigns/Clearing/SupportBlock/SupportBlock.styled';

import { Container as CourseSearchContainer } from 'Campaigns/Clearing/CourseSearch/CourseSearch.styled';

import IconButton from 'Campaigns/Clearing/Button/Icon';

export const StyledSection = styled(Section)`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
`;

export const Wrapper = styled.div`
    && {
        position: relative;
        width: 100%;
        max-width: 96.5rem;
        padding: 0;
    }
`;

export const Background = styled.div`
    ${({ theme }) => css`
        position: absolute;
        background: transparent
            linear-gradient(
                109deg,
                ${theme.colors.campaigns.clearing.primary.hex} 0%,
                ${theme.colors.accents[30].hex} 100%
            )
            0% 0% no-repeat padding-box;
        inset: 0;
        z-index: -1;
        height: 11rem;
        margin: 0 auto;
        pointer-events: none;

        @media ${theme.breakpoints.medium.media} {
            height: 21rem;
        }
    `}
`;

export const TileImageWrapper = styled.div`
    ${({ theme }) => css`
        position: absolute;
        max-height: 26.25rem;
        min-height: 25.625rem;
        z-index: -1;
        inset: 0;
        top: 11rem;
        margin: 0 auto;
        pointer-events: none;

        @media ${theme.breakpoints.medium.media} {
            max-height: 23rem;
            top: 21rem;
        }

        @media ${theme.breakpoints.xlarge.media} {
            max-height: 35.6rem;
        }
    `}
`;

export const MobileImage = styled(NextImage)`
    ${({ theme }) => css`
        display: block;
        object-fit: contain;
        transform: translateX(0.75rem);

        @media ${theme.breakpoints.medium.media} {
            display: none;
        }
    `}
`;

export const InnerContent = styled.div<{ $isClearingOpen?: boolean }>`
    ${({ theme, $isClearingOpen }) => css`
        position: relative;
        margin-top: 1.5rem;

        @media ${theme.breakpoints.medium.media} {
            margin-top: unset;
            padding: ${$isClearingOpen ? '3rem 0 11rem' : '3rem 0 0'};
        }

        @media ${theme.breakpoints.xlarge.media} {
            padding: ${$isClearingOpen ? ' 4.875rem 0 6rem' : '4.875rem 0 1rem'};
        }
    `}
`;

export const PreHeading = styled.span`
    ${({ theme }) => css`
        position: relative;
        font-size: 1rem;
        font-style: italic;
        line-height: 1;
        color: ${theme.colors.campaigns.clearing.primaryContrast.hex};

        @media ${theme.breakpoints.medium.media} {
            font-size: 1.5rem;
        }

        @media ${theme.breakpoints.xlarge.media} {
            font-size: 2rem;
        }
    `}
`;

export const SauceGap = styled.span`
    ${({ theme }) => css`
        padding-left: 4rem;

        @media ${theme.breakpoints.medium.media} {
            padding-left: 6.5rem;
        }

        @media ${theme.breakpoints.xlarge.media} {
            padding-left: 8.25rem;
        }
    `}
`;

export const StyledSauceImage = styled(NextImage)`
    ${({ theme }) => css`
        position: absolute;
        rotate: -4.5deg;
        width: 4rem;
        height: 1.9rem;
        left: 3.75rem;
        top: -0.75rem;
        object-fit: contain;

        @media ${theme.breakpoints.medium.media} {
            left: 5.95rem;
            top: -1.2rem;
            width: 6rem;
            height: 3rem;
        }

        @media ${theme.breakpoints.xlarge.media} {
            left: 7.65rem;
            top: -1.25rem;
            width: 7.9rem;
            height: 3.9rem;
        }
    `}
`;

export const Heading = styled.h1`
    ${({ theme }) => css`
        font-size: 2.25rem;
        line-height: 1;
        font-weight: 900;
        font-family: ${theme.fonts.secondary};
        font-style: italic;
        color: ${theme.colors.campaigns.clearing.primaryContrast.hex};
        margin: 0.5rem 0 2rem;
        max-width: 16rem;
        text-transform: uppercase;

        @media ${theme.breakpoints.medium.media} {
            padding-right: 1rem;
            margin-top: 0.25rem;
            font-size: 3rem;
            white-space: nowrap;
            max-width: 100%;
        }

        @media ${theme.breakpoints.xlarge.media} {
            font-size: 5rem;
            padding-right: 0;
        }
    `}
`;

export const ClearingOpenWrapper = styled.div<{ $isClearingOpen?: boolean }>`
    ${({ theme, $isClearingOpen }) => css`
        position: relative;
        z-index: 1;
        padding: 1.5rem 1rem;
        background-color: ${theme.colors.campaigns.clearing.primaryContrast.hex};
        font-size: 1.75rem;
        border-radius: 0.5rem;
        box-shadow: ${theme.commonStyles.boxShadow};
        margin-top: 6.3rem;

        ${!$isClearingOpen &&
        css`
            margin-top: 1.5rem;
        `}

        @media ${theme.breakpoints.medium.media} {
            position: absolute;
            font-size: 1.25rem;
            margin-top: 0.3125rem;
            padding: 1.5rem 1.25rem 1.5rem 0.75rem;
            max-width: 21.75rem;
        }

        @media ${theme.breakpoints.xlarge.media} {
            position: relative;
            font-size: 1.75rem;
            margin-top: 3.5rem;
            padding: 1.125rem 1.5rem;
            max-width: 43.75rem;
        }
    `}
`;

export const ContactsHeading = styled.div`
    ${({ theme }) => css`
        margin-top: 0;
        margin-bottom: 0.5rem;

        p:first-of-type {
            margin: 0;
            font-size: 1.25rem;
            font-weight: 500;
            line-height: 1.2;

            @media ${theme.breakpoints.xlarge.media} {
                font-size: 1.75rem;
            }
        }
    `}
`;

export const PhoneWrapper = styled.div`
    ${({ theme }) => css`
        margin-top: 1rem;
        display: flex;
        align-items: center;
        gap: 1rem;

        @media ${theme.breakpoints.medium.media} {
            gap: 0.75rem;
        }
    `}
`;

export const LinesOpen = styled.span`
    ${({ theme }) => css`
        font-size: 1rem;
        color: ${theme.colors.campaigns.clearing.secondary.hex};

        @media ${theme.breakpoints.large.media} {
            font-size: 1rem;
        }
    `}
`;

export const ContactsWrapper = styled.ul`
    ${({ theme }) => css`
        margin: 1rem 0;
        padding: 0;
        display: flex;
        flex-wrap: wrap;
        gap: 0.75rem;
        list-style: none;

        @media ${theme.breakpoints.large.media} {
            margin: 1rem 0 1.5625rem;
            gap: 0.75rem;
        }
    `}
`;

export const ContactItem = styled.li`
    ${({ theme }) => css`
        margin: 0;
        width: 100%;

        @media only screen and (min-width: 23.4375rem) {
            width: calc(50% - 0.5rem);
        }

        @media ${theme.breakpoints.medium.media} {
            width: unset;
        }
    `}
`;

export const ClearingPromiseWrapper = styled.div`
    ${({ theme }) => css`
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 0.5rem;
        color: ${theme.colors.campaigns.clearing.primary.hex};
    `}
`;

export const PromiseWrapper = styled.div<{ $isClearingOpen?: boolean }>`
    ${({ theme, $isClearingOpen }) => css`
        position: relative;
        margin-top: 1rem;
        width: 100%;

        @media ${theme.breakpoints.medium.media} {
            padding: ${$isClearingOpen ? '11.25rem 0 1.5rem' : '0'};
            margin-top: unset;
        }

        @media ${theme.breakpoints.xlarge.media} {
            padding: ${$isClearingOpen ? '1.5rem 0' : '0'};
            max-width: 96.5rem;
            margin-top: 0.5rem;
        }
    `}
`;

export const CourseSearchWrapper = styled.div`
    ${({ theme }) => css`
        display: none;
        height: 100%;
        width: 100%;

        @media ${theme.breakpoints.xlarge.media} {
            display: block;
        }
    `}
`;

export const StyledIcon = styled.svg`
    height: 1.5rem;
    width: 1.5rem;
`;

export const ClearingPromiseText = styled.p`
    ${({ theme }) => css`
        margin: 0;
        font-weight: 500;
        line-height: 1.2;
        font-size: 1rem;
        font-style: italic;

        @media ${theme.breakpoints.xlarge.media} {
            font-size: 1.125rem;
        }
    `}
`;

export const StyledIconButton = styled(IconButton)`
    ${({ theme }) => css`
        && {
            width: 100%;
            background: ${theme.colors.primaryContrast.hex};
            color: ${theme.colors.text.hex};

            @media only screen and (max-width: 23.4375rem) {
                gap: 0.5rem;
            }

            @media only screen and (max-width: 25rem) {
                padding: 0.75rem 0.225rem;
            }
        }
    `}
`;

export const StyledPhoneIconButton = styled(IconButton)`
    ${({ theme }) => css`
        && {
            background: ${theme.colors.campaigns.clearing.primary.hex};
            color: ${theme.colors.campaigns.clearing.primaryContrast.hex};
            white-space: nowrap;
            max-width: 10rem;

            a {
                font-size: 1.25rem;
            }

            @media ${theme.breakpoints.medium.media} {
                a {
                    font-size: 1rem;
                }
            }

            @media ${theme.breakpoints.xlarge.media} {
                max-width: 12.9rem;
                a {
                    font-size: 1.5rem;
                }
            }
        }
    `}
`;

export const MobileImageWrapper = styled.div`
    ${({ theme }) => css`
        position: absolute;
        right: 0;
        top: 0.5rem;

        @media ${theme.breakpoints.medium.media} {
            display: none;
        }
    `}
`;

export const TabletImage = styled(NextImage)`
    ${({ theme }) => css`
        display: none;
        object-fit: contain;

        @media ${theme.breakpoints.medium.media} {
            display: block;
        }

        @media ${theme.breakpoints.large.media} {
            display: none;
        }
    `}
`;

export const DesktopImage = styled(NextImage)`
    ${({ theme }) => css`
        display: none;
        object-fit: contain;

        @media ${theme.breakpoints.large.media} {
            display: block;
        }
    `}
`;

export const ImageWrapper = styled.div<{ $isClearingOpen?: boolean }>`
    ${({ theme, $isClearingOpen }) => css`
        display: ${$isClearingOpen ? 'block' : 'none'};
        position: absolute;
        right: 0;
        top: -12rem;
        width: 14.625rem;
        height: 16.5rem;
        z-index: -1;

        @media ${theme.breakpoints.medium.media} {
            display: block;
            right: 0;

            ${$isClearingOpen
                ? css`
                      top: 1rem;
                      height: 31.75rem;
                      width: 22.625rem;
                  `
                : css`
                      top: 5rem;
                      height: 32.75rem;
                      width: 24.625rem;
                  `};
        }

        @media (min-width: 54.25rem) {
            right: 3rem;

            ${$isClearingOpen
                ? css`
                      height: 34.75rem;
                      width: 25.625rem;
                  `
                : css`
                      height: 32.75rem;
                      width: 24.625rem;
                      top: 5rem;
                  `};
        }

        @media ${theme.breakpoints.large.media} {
            right: 0;
            height: 36rem;
            top: 1rem;

            ${$isClearingOpen
                ? css`
                      top: 1rem;
                      width: 35.75rem;
                  `
                : css`
                      top: 3rem;
                      width: 30.75rem;
                  `};
        }

        @media ${$isClearingOpen ? theme.breakpoints.huge.media : theme.breakpoints.xlarge.media} {
            ${$isClearingOpen
                ? css`
                      top: 0.25rem;
                      height: 40rem;
                      width: 40rem;
                  `
                : css`
                      top: 4rem;
                      height: 43.45rem;
                      width: 40.45rem;
                  `};
        }
    `}
`;

export const ContainerIframe = styled.div<{ $isClearingOpen?: boolean }>`
    ${({ theme, $isClearingOpen }) => css`
        position: relative;
        width: 100%;
        background: ${theme.colors.primaryContrast.hex};
        padding: 1rem;
        margin: 6.3rem 0 0rem;
        border-radius: 0.5rem;
        box-shadow: ${theme.commonStyles.boxShadow};

        @media ${theme.breakpoints.medium.media} {
            max-width: 44rem;
            margin: ${$isClearingOpen ? '2rem 0' : '1rem 0'};
            width: ${$isClearingOpen ? '90%' : '70%'};
        }

        @media ${theme.breakpoints.large.media} {
            width: 90%;
        }
    `}
`;

export const SupportBlockWrapper = styled.div`
    ${({ theme }) => css`
        position: relative;
        width: 100%;

        ${SourceSupportBlockWrapper} {
            width: 100%;
        }

        ${SupportBlockContainer} {
            background-color: white;
            border-radius: 0.5rem;
            box-shadow: ${theme.commonStyles.boxShadow};
        }

        ${CourseSearchContainer} {
            margin-top: 1rem;
        }

        @media ${theme.breakpoints.medium.media} {
            ${SupportBlockContainer} {
                padding: 1.5rem 2.125rem 1.5rem 1rem;

                div:first-of-type {
                    padding-right: 2.7rem;
                }
            }

            ${CourseSearchContainer} {
                display: block;
                position: absolute;
                bottom: 2rem;
                right: -15rem;
                width: 19rem;
                margin-top: unset;
            }
        }

        @media (min-width: 54.25rem) {
            ${CourseSearchContainer} {
                margin-top: unset;
                right: -17rem;
                width: 20rem;
            }
        }

        @media ${theme.breakpoints.large.media} {
            ${CourseSearchContainer} {
                width: 25rem;
                right: -22rem;
            }
        }

        @media ${theme.breakpoints.xlarge.media} {
            max-width: 49rem;

            ${CourseSearchContainer} {
                display: none;
            }

            ${SupportBlockContainer} {
                padding-top: 1rem;
            }
        }
    `}
`;

export const FormHeading = styled.h2`
    ${({ theme }) => css`
        ${campaignHeading()};
        line-height: 1;
        font-weight: 900;
        color: ${theme.colors.campaigns.clearing.primary.hex};
        margin: 0;
        text-transform: uppercase;
    `}
`;
